/**
 * Exports menu (Wave 5): deliverable download buttons for the active MEP
 * system — BoQ CSV, riser SVG (fire-alarm only), PDF report, DXF — with
 * discipline-appropriate enable/disable + tooltips (logic in ../mep/exports),
 * plus an inline riser preview modal for fire systems.
 *
 * The riser SVG is fetched as text and shown through a blob `<img>` — the
 * markup is never injected into the document, so untrusted SVG content
 * cannot run scripts in the page context.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { getRiserSvg } from "../api/client";
import type { MepSystem } from "../api/types";
import { exportEntries } from "../mep/exports";

export interface ExportsMenuProps {
  system: MepSystem | null;
  modelId: string;
  /** Injectable for tests; defaults to the API client (global fetch). */
  fetchRiserSvg?: typeof getRiserSvg;
}

type PreviewState =
  | { phase: "closed" }
  | { phase: "loading" }
  | { phase: "ready"; blobUrl: string }
  | { phase: "failed"; message: string };

const linkStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "0.35rem 0.7rem",
  borderRadius: 6,
  border: "1px solid #4a90d9",
  background: "transparent",
  color: "#4a90d9",
  fontSize: "0.82rem",
  fontWeight: 600,
  textDecoration: "none",
  cursor: "pointer",
};

const disabledStyle: CSSProperties = {
  ...linkStyle,
  border: "1px solid #44446a",
  color: "#555577",
  cursor: "not-allowed",
};

export function ExportsMenu({ system, modelId, fetchRiserSvg = getRiserSvg }: ExportsMenuProps) {
  const entries = exportEntries(system, modelId);
  const fire = system?.discipline === "fire_alarm";
  const [preview, setPreview] = useState<PreviewState>({ phase: "closed" });
  const blobUrlRef = useRef<string | null>(null);

  const releaseBlob = useCallback(() => {
    if (blobUrlRef.current !== null) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  // Revoke any outstanding blob URL on unmount.
  useEffect(() => releaseBlob, [releaseBlob]);

  const closePreview = () => {
    releaseBlob();
    setPreview({ phase: "closed" });
  };

  const openPreview = async () => {
    if (!system) return;
    setPreview({ phase: "loading" });
    try {
      const svg = await fetchRiserSvg(system.id);
      releaseBlob();
      const blobUrl = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
      blobUrlRef.current = blobUrl;
      setPreview({ phase: "ready", blobUrl });
    } catch (err) {
      setPreview({
        phase: "failed",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  };

  return (
    <section
      data-testid="exports-menu"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        flexWrap: "wrap",
        margin: "0.6rem 0",
      }}
    >
      <span style={{ color: "#8e8eb8", fontSize: "0.8rem", letterSpacing: "0.06em" }}>
        EXPORTS
      </span>
      {entries.map((entry) =>
        entry.enabled ? (
          <a
            key={entry.key}
            // Keep the Wave 3 e2e testid for the BoQ link.
            data-testid={entry.key === "boq_csv" ? "boq-download" : `${entry.key}-download`}
            href={entry.href}
            download={entry.filename}
            title={entry.tooltip}
            style={linkStyle}
          >
            {entry.label}
          </a>
        ) : (
          <span
            key={entry.key}
            data-testid={entry.key === "boq_csv" ? "boq-download" : `${entry.key}-download`}
            aria-disabled="true"
            title={entry.tooltip}
            style={disabledStyle}
          >
            {entry.label}
          </span>
        ),
      )}
      {fire && (
        <button
          type="button"
          data-testid="riser-preview-button"
          onClick={() => void openPreview()}
          disabled={preview.phase === "loading"}
          title="Preview the riser diagram inline"
          style={linkStyle}
        >
          {preview.phase === "loading" ? "Loading riser…" : "Preview riser"}
        </button>
      )}

      {preview.phase !== "closed" && (
        <div
          data-testid="riser-preview-modal"
          role="dialog"
          aria-label="Riser diagram preview"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(10, 10, 20, 0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
          onClick={closePreview}
        >
          <div
            style={{
              background: "#21213a",
              borderRadius: 8,
              padding: "1rem",
              maxWidth: "85vw",
              maxHeight: "85vh",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "0.6rem",
                gap: "1rem",
              }}
            >
              <strong style={{ fontSize: "0.95rem" }}>Riser diagram</strong>
              <button
                type="button"
                data-testid="riser-preview-close"
                onClick={closePreview}
                style={{ ...linkStyle, padding: "0.2rem 0.6rem" }}
              >
                Close
              </button>
            </div>
            {preview.phase === "loading" && <p style={{ color: "#8e8eb8" }}>Loading riser…</p>}
            {preview.phase === "failed" && (
              <p data-testid="riser-preview-error" style={{ color: "#e06c75" }}>
                Could not load the riser diagram: {preview.message}
              </p>
            )}
            {preview.phase === "ready" && (
              <img
                data-testid="riser-preview-img"
                src={preview.blobUrl}
                alt="Riser diagram"
                style={{ maxWidth: "80vw", maxHeight: "75vh", background: "#fff", borderRadius: 4 }}
              />
            )}
          </div>
        </div>
      )}
    </section>
  );
}
